import { api } from './api';
import type { PaginatedResponse } from '../types';
import type {
  Permission,
  PermissionCreateRequest,
  PermissionUpdateRequest,
  PermissionSet,
  PermissionSetCreateRequest,
  PermissionSetUpdateRequest,
  Role,
  RoleCreateRequest,
  RoleUpdateRequest,
  RoleBinding,
  RoleBindingCreateRequest,
  RoleBindingUpdateRequest,
  Assignment,
  AssignmentCreateRequest,
  AssignmentUpdateRequest,
  AppItem,
  UserItem,
  EntityDependencies,
} from '../types/rbac';

// Standard backend envelope (matches departments.service.ts)
interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

export interface RbacQueryParams {
  search?: string;
  page?: number;
  page_size?: number;
  /** JSON per-column filters (see SaasDataTable.buildColumnFiltersParam). */
  filters?: string;
  /** Filter role-bindings / user-access to one project (derived from the binding's unit.appId). */
  app_id?: string;
  /** Assignments only: restrict to one user's role bindings. */
  user_id?: string;
  /** Also include global/core rows (the "show global" toggle); default own project only. */
  include_global?: boolean;
}

/**
 * Factory that builds a CRUD service for a given resource base path following the
 * `{base}/list | /create | /get/{id} | /update/{id} | /delete/{id} | /restore/{id}`
 * convention and the `{ success, code, message, data }` envelope.
 */
function createCrudService<TItem, TCreate, TUpdate>(base: string) {
  return {
    list: async (params?: RbacQueryParams): Promise<PaginatedResponse<TItem>> => {
      const response = await api.get<PaginatedResponse<TItem>>(`${base}/list`, { params });
      return response.data;
    },
    getById: async (id: string): Promise<TItem> => {
      const response = await api.get<ApiResponse<TItem>>(`${base}/get/${id}`);
      return response.data.data;
    },
    create: async (payload: TCreate): Promise<TItem> => {
      const response = await api.post<ApiResponse<TItem>>(`${base}/create`, payload);
      return response.data.data;
    },
    update: async (id: string, payload: TUpdate): Promise<TItem> => {
      const response = await api.put<ApiResponse<TItem>>(`${base}/update/${id}`, payload);
      return response.data.data;
    },
    remove: async (id: string): Promise<{ success: boolean; message: string }> => {
      const response = await api.delete<ApiResponse<{ success: boolean; message: string }>>(
        `${base}/delete/${id}`
      );
      return response.data.data;
    },
    restore: async (id: string): Promise<TItem> => {
      const response = await api.patch<ApiResponse<TItem>>(`${base}/restore/${id}`);
      return response.data.data;
    },
    /** Convenience: fetch all rows for dropdowns / cross-referencing (no pagination).
     * Optionally scope to one project (+ global) — used by the role picker so a role
     * in project X can only be offered permission sets from project X (or global). */
    listAll: async (
      search?: string,
      opts?: { appId?: string | null; includeGlobal?: boolean }
    ): Promise<TItem[]> => {
      const response = await api.get<PaginatedResponse<TItem>>(`${base}/list`, {
        params: {
          page: 1,
          page_size: 1000,
          search,
          app_id: opts?.appId || undefined,
          include_global: opts?.includeGlobal || undefined,
        },
      });
      return response.data.data || [];
    },
    /**
     * What still references this record. Used to block deletion (and explain why)
     * and to show "used by" links. Buckets are always present; `canDelete` is false
     * whenever anything depends on it. Not available for leaf resources (assignments).
     */
    getDependencies: async (id: string): Promise<EntityDependencies> => {
      const response = await api.get<ApiResponse<EntityDependencies>>(`${base}/dependencies/${id}`);
      return response.data.data;
    },
    /** Preview moving this record to a project (empty appId = global). Platform-admin action. */
    getRescopePreview: async (id: string, appId?: string | null): Promise<RbacRescopePreview> => {
      const response = await api.get<ApiResponse<RbacRescopePreview>>(`${base}/${id}/rescope-preview`, {
        params: { app_id: appId || undefined },
      });
      return response.data.data;
    },
    /** Change this record's owning project (null/empty = global). Blocks if it would orphan refs. */
    rescopeProject: async (id: string, appId: string | null): Promise<TItem> => {
      const response = await api.patch<ApiResponse<TItem>>(`${base}/${id}/project`, { appId: appId || null });
      return response.data.data;
    },
  };
}

/** Re-scope preview shape for RBAC roles / permission sets. */
export interface RbacRescopePreview {
  canRescope: boolean;
  conflicts: Record<string, Record<string, number>>;
  references: Record<string, Record<string, number>>;
}

export const permissionsApi = createCrudService<
  Permission,
  PermissionCreateRequest,
  PermissionUpdateRequest
>('/api/v1/auth-permissions');

export const permissionSetsApi = createCrudService<
  PermissionSet,
  PermissionSetCreateRequest,
  PermissionSetUpdateRequest
>('/api/v1/auth-permission-sets');

export const rolesApi = createCrudService<Role, RoleCreateRequest, RoleUpdateRequest>(
  '/api/v1/auth-roles'
);

export const roleBindingsApi = createCrudService<
  RoleBinding,
  RoleBindingCreateRequest,
  RoleBindingUpdateRequest
>('/api/v1/auth-role-bindings');

export const assignmentsApi = createCrudService<
  Assignment,
  AssignmentCreateRequest,
  AssignmentUpdateRequest
>('/api/v1/auth-user-role-assignments');

// ---------------------------------------------------------------------------
// Dropdown helpers (Apps / Users)
// ---------------------------------------------------------------------------

export const appsApi = {
  list: async (): Promise<AppItem[]> => {
    const response = await api.get<PaginatedResponse<AppItem>>('/api/v1/apps/list', {
      params: { page: 1, page_size: 1000 },
    });
    return response.data.data || [];
  },
};

export const usersApi = {
  list: async (): Promise<UserItem[]> => {
    const response = await api.get<PaginatedResponse<UserItem>>('/api/v1/users/list', {
      params: { page: 1, page_size: 1000 },
    });
    return response.data.data || [];
  },
};
